/**
 * Wishlist client-side store for Trade theme.
 *
 * Responsibility
 * - Persist wishlist data in the browser (currently localStorage only).
 * - Sync wishlist buttons on product cards / product page.
 * - Render the wishlist page using the `card-product` view.
 *
 * Single source of truth
 * - Storage behaviour is configured at theme level via:
 *   - settings.enable_wishlist (master on/off switch; asset load is gated in layout/theme.liquid)
 *   - settings.wishlist_mode ("local_only" vs "future_account_based")
 * - This file currently implements ONLY the local browser variant.
 *
 * Future account-based sync (DO NOT IMPLEMENT YET)
 * - When `settings.wishlist_mode == 'future_account_based'` and a customer is logged in,
 *   a higher-level integration can:
 *   - Read from a remote wishlist service keyed by customer.id.
 *   - Merge any existing local-only entries into the account store once.
 *   - Call setWishlist(...) with the merged data so the UI stays in sync.
 * - No Shopify API or customer logic is wired here yet by design.
 */
(function () {
  console.log('[wishlist] file loaded');
  var STORAGE_KEY = 'trade:wishlist';
  var VERSION = 1;

  function nowIso() {
    return new Date().toISOString();
  }

  function createEmptyWishlist() {
    return {
      version: VERSION,
      updated_at: nowIso(),
      items: []
    };
  }

  function loadWishlist() {
    try {
      if (!('localStorage' in window)) {
        return createEmptyWishlist();
      }

      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return createEmptyWishlist();
      }

      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') {
        return createEmptyWishlist();
      }

      if (typeof data.version !== 'number') {
        data.version = VERSION;
      }

      if (!Array.isArray(data.items)) {
        data.items = [];
      }

      if (!data.updated_at) {
        data.updated_at = nowIso();
      }

      return data;
    } catch (e) {
      return createEmptyWishlist();
    }
  }

  /**
   * Abstraction layer: read the current wishlist snapshot.
   *
   * Today this is a thin wrapper around loadWishlist() and always returns the
   * local browser store. In the future this is the single hook where
   * account-based data (customer.id) can be introduced.
   */
  function getWishlist() {
    return loadWishlist();
  }

  function saveWishlist(data) {
    try {
      if (!('localStorage' in window)) {
        return;
      }

      data.version = VERSION;
      data.updated_at = nowIso();
      console.log('[wishlist] saved', data);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // Fail silently if storage is unavailable
    }
  }

  /**
   * Abstraction layer: write a wishlist snapshot.
   *
   * This is the only place that should mutate persisted wishlist data so
   * that future remote sync can be hooked in without touching UI wiring.
   */
  function setWishlist(next) {
    if (!next || typeof next !== 'object') {
      next = createEmptyWishlist();
    }
    saveWishlist(next);
    return next;
  }

  function findItemIndex(wishlist, productId, variantId) {
    var items = wishlist.items || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var sameProduct = String(item.product_id) === String(productId);
      var sameVariant;

      if (item.variant_id == null && variantId == null) {
        sameVariant = true;
      } else {
        sameVariant = String(item.variant_id) === String(variantId);
      }

      if (sameProduct && sameVariant) {
        return i;
      }
    }

    return -1;
  }

  function isWishlisted(wishlist, productId, variantId) {
    return findItemIndex(wishlist, productId, variantId) !== -1;
  }

  function addToWishlist(wishlist, productId, variantId, productHandle) {
    if (isWishlisted(wishlist, productId, variantId)) {
      return wishlist;
    }

    if (!Array.isArray(wishlist.items)) {
      wishlist.items = [];
    }

    wishlist.items.push({
      product_id: productId,
      product_handle: productHandle == null ? null : String(productHandle),
      variant_id: variantId == null ? null : variantId,
      added_at: nowIso(),
      metadata: {}
    });

    return wishlist;
  }

  function removeFromWishlist(wishlist, productId, variantId) {
    var index = findItemIndex(wishlist, productId, variantId);
    if (index === -1) {
      return wishlist;
    }

    wishlist.items.splice(index, 1);
    return wishlist;
  }

  function parseProductId(value) {
    if (value == null) {
      return null;
    }

    var numeric = parseInt(value, 10);
    if (!isNaN(numeric)) {
      return numeric;
    }

    return String(value);
  }

  var eventsBound = false;

  function bindEvents() {
    if (eventsBound) return;
    eventsBound = true;

    document.addEventListener('click', function (e) {
      var button = e.target.closest('.card__wishlist[data-product-id]');
      if (!button) return;

      e.preventDefault();
      e.stopPropagation();

      var productId = parseProductId(button.getAttribute('data-product-id'));
      var productHandle = button.getAttribute('data-product-handle') || null;

      if (productId == null) return;

      var currentWishlist = getWishlist();
      var currentlyWishlisted = isWishlisted(currentWishlist, productId, null);

      if (currentlyWishlisted) {
        currentWishlist = removeFromWishlist(currentWishlist, productId, null);
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');

        if (typeof window.publish === 'function') {
          window.publish('wishlist:removed', { productId: productId });
        }

        var cardWrapper = button.closest('.card-wrapper');
        if (document.querySelector('.wishlist-page') && cardWrapper) {
          button.classList.add('is-removing');
          setTimeout(function () {
            cardWrapper.remove();
            updateWishlistEmptyState(document.querySelector('.wishlist-page'), currentWishlist.items.length > 0);
          }, 300);
        }
      } else {
        if (!productHandle) return;
        currentWishlist = addToWishlist(currentWishlist, productId, null, productHandle);
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');

        if (typeof window.publish === 'function') {
          window.publish('wishlist:added', { productId: productId, handle: productHandle });
        }
      }

      setWishlist(currentWishlist);
      if (typeof window.publish === 'function') {
        window.publish('wishlist:updated', currentWishlist);
      }
    });
  }

  function syncButtons() {
    var buttons = document.querySelectorAll('.card__wishlist[data-product-id]');
    if (!buttons || buttons.length === 0) {
      return;
    }

    var wishlist = getWishlist();

    Array.prototype.forEach.call(buttons, function (button) {
      var productId = parseProductId(button.getAttribute('data-product-id'));
      if (productId == null) return;

      // Set initial state
      var isInWishlist = isWishlisted(wishlist, productId, null);
      button.classList.toggle('is-active', isInWishlist);
      button.setAttribute('aria-pressed', isInWishlist ? 'true' : 'false');
    });
  }

  function updateWishlistEmptyState(pageElement, hasItems) {
    if (!pageElement) return;
    var emptyElement = pageElement.querySelector('.wishlist-empty');
    var itemsElement = pageElement.querySelector('.wishlist-items');
    if (emptyElement) emptyElement.hidden = hasItems;
    if (itemsElement) itemsElement.style.display = hasItems ? '' : 'none';
  }

  /**
   * Update every header wishlist badge in real-time.
   *
   * The header renders: <span class="header__icon-badge header__icon-badge--wishlist" hidden>0</span>
   * This function reads the live count from localStorage and:
   *   - Sets the text content to the current count
   *   - Shows the badge (removes `hidden`) when count > 0
   *   - Hides the badge (adds `hidden`) when count === 0
   *
   * Called on:
   *   - Page load (initWishlist)
   *   - Every wishlist:updated event (add AND remove)
   *   - wishlist:viewed event (to reflect any removals done on the wishlist page)
   */
  function updateWishlistBadge() {
    var badges = document.querySelectorAll('.header__icon-badge--wishlist');
    if (!badges || badges.length === 0) return;

    var count = (getWishlist().items || []).length;

    Array.prototype.forEach.call(badges, function (badge) {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.hidden = count === 0;
      // Keep aria-hidden in sync: badge is decorative so screen readers
      // get the count only from the icon aria-label — badge stays aria-hidden=true always.
      badge.setAttribute('aria-hidden', 'true');
    });
  }

  /**
   * Batch render wishlist items using the Section Rendering API.
   * This replaces the N-request loop with a single call.
   */
  function renderWishlistPage() {
    var pageElement = document.querySelector('.wishlist-page');
    if (!pageElement) return;

    var itemsContainer = pageElement.querySelector('.wishlist-items');
    if (!itemsContainer) return;

    var wishlist = getWishlist();
    var items = wishlist.items || [];

    if (items.length === 0) {
      updateWishlistEmptyState(pageElement, false);
      itemsContainer.innerHTML = '';
      return;
    }

    // Mark as loading for skeleton state
    pageElement.classList.add('is-loading');

    // Build query for search API to get product cards in one go
    // Note: We use handle-based search as it's most reliable for rendering snippets.
    var handles = items.map(function (i) { return 'handle:"' + i.product_handle + '"'; }).join(' OR ');
    var url = '/search?q=' + encodeURIComponent(handles) + '&section_id=wishlist-grid';

    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        // Section rendering API returns just the content of the section
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newContent = doc.querySelector('.wishlist-items');

        if (newContent) {
          itemsContainer.innerHTML = newContent.innerHTML;
          updateWishlistEmptyState(pageElement, true);
        } else {
          updateWishlistEmptyState(pageElement, false);
        }

        pageElement.classList.remove('is-loading');
        syncButtons();

        // Mark wishlist as "viewed" to clear header badges
        if (typeof window.publish === 'function') {
          window.publish('wishlist:viewed');
        }
      })
      .catch(function (err) {
        console.error('[wishlist] render error:', err);
        pageElement.classList.remove('is-loading');
      });
  }

  function initWishlist() {
    bindEvents();
    syncButtons();
    renderWishlistPage();

    // Paint the header badge with the current localStorage count on page load.
    // This handles the case where the user already has items wishlisted from
    // a previous session (the server always renders the badge as hidden="true").
    updateWishlistBadge();

    // Listen for external updates (e.g. from other tabs or components)
    if (typeof window.subscribe === 'function') {
      window.subscribe('wishlist:updated', function () {
        // Real-time badge sync: fires on BOTH add and remove.
        // This is the fix for the badge not decrementing on un-like.
        updateWishlistBadge();
        if (!document.querySelector('.wishlist-page')) syncButtons();
      });
      window.subscribe('wishlist:viewed', function () {
        // When the wishlist page is visited, items may have been removed;
        // re-sync the badge to reflect the current state.
        updateWishlistBadge();
      });
      window.subscribe('wishlist:sync', function () {
        syncButtons();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist);
  } else {
    initWishlist();
  }
})();
